<script setup>
import { HIcon, HInfoModal, HRow } from '@pixul/hephaestus';
import LanguageToggle              from '@/components/Navbar/LanguageToggle.vue';
</script>

<template>
  <HInfoModal
    dark
    only-title
    position='bottom'
    timeout='700'
  >
    <template #activator>
      <a
        :class='classes'
        :href='item.path'
        :target="item.name === 'profile' ? '_target' : '_self'"
        @click='e => onClick(e, item.name)'
        @mouseenter='onEnter'
        @mouseleave='onLeave'
      >
        <LanguageToggle v-if="item.name === 'language'" />

        <HRow
          v-else
          align-items='center'
          gap='14'
        >
          <HIcon
            v-if='item.icon?.name'
            :color='item.icon.color'
            :icon='item.icon.name'
            :size='onlyIcon && !isMobile ? 24 : 16'
          />
          <p v-if='!onlyIcon || isMobile'>{{ $t(`menu.${ item.name }`) }}</p>
        </HRow>
        <HIcon
          v-if='item.children'
          :class="open ? 'navbar-item__icon--opened' : ''"
          icon='chevron-down'
        />
      </a>
    </template>
    <template
      v-if='tooltip'
      #default
    >
      {{ tooltip }}
    </template>
  </HInfoModal>
</template>

<script>
import { mapStores }    from 'pinia';
import { useUserStore } from '@/store/user';

export default {
  name : 'NavbarItemContent',

  props : {
    item : {
      type    : Object,
      default : () => {}
    },

    hoverItem : {
      type    : String,
      default : null
    },

    open : {
      type    : Boolean,
      default : false
    },

    isMobile : {
      type    : Boolean,
      default : false
    },

    isChild : {
      type    : Boolean,
      default : false
    },

    onlyIcon : {
      type    : Boolean,
      default : false
    },

    tooltip : {
      type    : String,
      default : ''
    }
  },

  emits : ['update:hoverItem', 'itemClick'],

  computed : {
    ...mapStores(useUserStore),

    classes() {

      const classes = ['navbar-item'];

      if (this.isActive || this.isChildActive) {

        classes.push('navbar-item--active');
      }

      if (this.isHover || this.open) {

        classes.push('navbar-item--hover');
      }

      if (this.isMobile) {

        classes.push('navbar-item--mobile');
      }

      if (this.isChild) {

        classes.push('margin-left-40');
      }

      if (this.item.icon?.color) {

        classes.push(`navbar-item--${ this.item.icon.color }`);
      }

      return classes;
    },

    isHover() {

      return this.hoverItem === this.item.name;
    },

    isActive() {

      const currentPath = this.$router.currentRoute.value.name;
      return this.item.name === currentPath;
    },

    isChildActive() {

      if (!this.item.children) {

        return false;
      }

      const currentPath = this.$router.currentRoute.value.name;
      const parentPath = currentPath?.split('-')[0];

      return this.item.name.includes(parentPath);
    }
  },

  methods : {
    onEnter() {

      this.$emit('update:hoverItem', this.item.name);
    },

    onLeave() {

      this.$emit('update:hoverItem', null);
    },

    onClick(e, itemName) {

      if (itemName === 'profile') {

        return;
      }

      e.preventDefault();
      this.$emit('itemClick');
    }
  }
};
</script>

<style lang='scss' rel='stylesheet/scss' scoped>
.navbar-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 7px;
  padding: 8px 10px;
  cursor: pointer;
  border-radius: 4px;
  background-color: var(--grey-100);
  transition: 0.1s background-color linear;

  p {
    transition: 0.1s color linear;
  }

  svg {
    transition: 0.1s color linear, 0.2s transform ease-in-out;
  }
}

.navbar-item__icon--opened {
  transform: rotate(180deg);
}

.navbar-item--active {
  p, svg {
    font-weight: bold;
    color: var(--blue-400)
  }
}

.navbar-item--hover {
  background-color: var(--grey-300);
  color: var(--blue-400);

  p, svg {
    color: var(--blue-400);
  }
}

.navbar-item--mobile {
  height: 40px;

  &.navbar-item--hover {
    background: initial;
  }
}

.navbar-item--red {
  &.navbar-item--hover {
    p, svg {
      color: red;
    }
  }
}
</style>
